import React, { useState, useRef } from 'react';
import { motion, useInView } from 'framer-motion';
import { SparklesIcon, RocketIcon, LayersIcon, LayoutTemplateIcon, PaintbrushIcon, NavigationIcon, SearchIcon } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import { ContactFormPopup } from './ContactFormPopup';
const bulletIcons = [LayoutTemplateIcon, PaintbrushIcon, NavigationIcon, SearchIcon];
export function Services() {
  const ref = useRef(null);
  const isInView = useInView(ref, {
    once: true,
    margin: '-100px'
  });
  const {
    t
  } = useLanguage();
  const [isPopupOpen, setIsPopupOpen] = useState(false);
  const cards = [{
    icon: SparklesIcon,
    titleKey: 'pricing.demo.title',
    descKey: 'pricing.demo.desc',
    bullets: ['pricing.demo.b1', 'pricing.demo.b2', 'pricing.demo.b3'],
    price: '$0',
    ctaKey: 'pricing.demo.cta',
    label: t('pricing.demo.label') || 'FREE'
  }, {
    icon: RocketIcon,
    titleKey: 'pricing.single.title',
    descKey: 'pricing.single.desc',
    bullets: ['pricing.single.b1', 'pricing.single.b2', 'pricing.single.b3', 'pricing.single.b4'],
    priceKey: 'pricing.single.price',
    ctaKey: 'pricing.single.cta'
  }, {
    icon: LayersIcon,
    titleKey: 'pricing.multi.title',
    descKey: 'pricing.multi.desc',
    bullets: ['pricing.multi.b1', 'pricing.multi.b2', 'pricing.multi.b3', 'pricing.multi.b4'],
    priceKey: 'pricing.multi.price',
    ctaKey: 'pricing.multi.cta'
  }];
  return <>
      <section id="services" ref={ref} className="relative w-full overflow-hidden px-4 py-16 sm:py-24" style={{
      backgroundColor: 'var(--bg-primary)'
    }} data-id="element-253">
        <div className="relative z-10 mx-auto max-w-7xl" data-id="element-254">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={isInView ? {
          opacity: 1,
          y: 0
        } : {}} transition={{
          duration: 0.6
        }} className="mb-12 sm:mb-16 text-center" data-id="element-255">
            <h2 className="text-3xl md:text-4xl" style={{
            color: 'var(--text-primary)'
          }} data-id="element-256">
              {t('pricing.title')} {t('pricing.titleHighlight')}
            </h2>
          </motion.div>

          <div className="grid gap-6 lg:grid-cols-3 max-w-7xl mx-auto" data-id="element-257">
            {cards.map((card, cardIndex) => <motion.div key={cardIndex} initial={{
            opacity: 0,
            y: 30
          }} animate={isInView ? {
            opacity: 1,
            y: 0
          } : {}} transition={{
            duration: 0.6,
            delay: 0.1 + cardIndex * 0.1
          }} data-id="element-258">
                <div className="h-full rounded-3xl border p-6 sm:p-8 flex flex-col relative" style={{
              borderColor: 'var(--border)',
              backgroundColor: 'var(--bg-secondary)'
            }} data-id="element-259">
                  {/* Label centered at top */}
                  {card.label && <div className="absolute top-4 left-1/2 -translate-x-1/2 px-3 py-1 rounded-full text-xs font-medium tracking-wider uppercase" style={{
                backgroundColor: 'var(--accent)',
                color: '#fff'
              }} data-id="element-260">
                      {card.label}
                    </div>}

                  {/* Spacer — same height for all cards to keep content aligned */}
                  <div className="h-8" data-id="element-261" />

                  {/* Icon + Title row */}
                  <div className="flex items-center gap-3 mb-7" data-id="element-262">
                    <div className="p-2.5 rounded-xl flex-shrink-0" style={{
                  backgroundColor: 'var(--bg-tertiary)'
                }} data-id="element-263">
                      <card.icon className="w-5 h-5" style={{
                    color: 'var(--accent)'
                  }} data-id="element-264" />
                    </div>
                    <h3 className="text-base sm:text-lg font-medium" style={{
                  color: 'var(--text-primary)'
                }} data-id="element-265">
                      {t(card.titleKey)}
                    </h3>
                  </div>

                  {/* Description */}
                  <p className="text-sm sm:text-base mb-8" style={{
                color: 'var(--text-secondary)',
                minHeight: '4em'
              }} data-id="element-266">
                    {t(card.descKey)}
                  </p>

                  {/* Bullet list — consistent height across cards */}
                  <ul className="space-y-3.5 mb-8 flex-1" style={{
                minHeight: '10rem'
              }} data-id="element-267">
                    {card.bullets.map((bulletKey, i) => {
                  const text = t(bulletKey);
                  if (!text || text === bulletKey) return null;
                  const Icon = bulletIcons[i] || bulletIcons[0];
                  return <li key={i} className="flex items-start gap-3" data-id="element-268">
                          <div className="mt-0.5 flex-shrink-0 w-6 h-6 rounded-full flex items-center justify-center" style={{
                      backgroundColor: 'var(--bg-tertiary)'
                    }} data-id="element-269">
                            <Icon className="w-3.5 h-3.5" style={{
                        color: 'var(--accent)'
                      }} data-id="element-270" />
                          </div>
                          <span className="text-sm sm:text-base" style={{
                      color: 'var(--text-secondary)'
                    }} data-id="element-271">
                            {text}
                          </span>
                        </li>;
                })}
                  </ul>

                  {/* Price + CTA */}
                  <div className="mt-auto pt-6 border-t" style={{
                borderColor: 'var(--border)'
              }} data-id="element-272">
                    <div className="mb-5 text-center" data-id="element-273">
                      <span className="text-2xl sm:text-3xl font-medium" style={{
                    color: 'var(--text-primary)'
                  }} data-id="element-274">
                        {card.price || t(card.priceKey!)}
                      </span>
                    </div>
                    <button onClick={() => setIsPopupOpen(true)} className="block w-full rounded-xl py-4 text-center text-sm sm:text-base font-medium transition-all hover:scale-[1.02]" style={{
                  backgroundColor: 'var(--accent)',
                  color: '#FFFFFF'
                }} data-id="element-275">
                      {t(card.ctaKey)}
                    </button>
                  </div>
                </div>
              </motion.div>)}
          </div>
        </div>
      </section>

      <ContactFormPopup isOpen={isPopupOpen} onClose={() => setIsPopupOpen(false)} data-id="element-276" />
    </>;
}